import { useEffect, useState } from 'react';
import { formatSlotDayShort } from '../widget-lib/format-date';
import { useEmbedBootstrap } from '../use-widget-bootstrap';

import { ApiError } from '../widget-api/http-client';
import { fetchClientServices, type ClientServiceListItem } from '../widget-api/client-services';
import {
  createScheduleSlot,
  moveScheduleSlot,
  notifyScheduleSlot,
  patchScheduleSlot,
  validateScheduleSlotMove,
  type ScheduleSlotListItem
} from '../widget-api/schedule-slots';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import {
  listAdminClients,
  createBookingForClient,
  type AdminClientListItem
} from '../widget-api/trainer-clients';

export type TrainerScheduleStaffDialogMode = 'create' | 'edit' | 'move' | 'notify' | null;

type Props = {
  mode: TrainerScheduleStaffDialogMode;
  onClose: () => void;
  accessToken: string;
  organizationId: number;
  branchId: number;
  trainerUserId: number | null;
  slot: ScheduleSlotListItem | null;
  onCommitted: () => void;
  initialPersonal?: boolean;
};

function toHm(isoTime: string | undefined): string {
  const t = (isoTime || '').trim();
  if (t.length >= 5) return t.slice(0, 5);
  return '10:00';
}

export function TrainerScheduleStaffDialog({
  mode,
  onClose,
  accessToken,
  organizationId,
  branchId,
  trainerUserId,
  slot,
  onCommitted,
  initialPersonal
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState<ClientServiceListItem[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const { me } = useEmbedBootstrap();
  const [serviceId, setServiceId] = useState('');
  const [slotDate, setSlotDate] = useState('');
  const [startHm, setStartHm] = useState('10:00');
  const [endHm, setEndHm] = useState('11:00');
  const [maxPart, setMaxPart] = useState('8');

  const [selectedBranchId, setSelectedBranchId] = useState(branchId);
  const [isPersonal, setIsPersonal] = useState(initialPersonal ?? false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<AdminClientListItem[]>([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AdminClientListItem | null>(null);
  const [personalComment, setPersonalComment] = useState('');

  const [notifyEvent, setNotifyEvent] = useState('slot_reminder');
  const [notifyMessage, setNotifyMessage] = useState('');

  useEffect(() => {
    setSelectedBranchId(branchId);
  }, [branchId]);

  useEffect(() => {
    if (mode !== 'create' || !selectedBranchId) return;
    setServicesLoading(true);
    fetchClientServices({ organizationId, branchId: selectedBranchId, accessToken })
      .then(setServices)
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, [mode, selectedBranchId, organizationId, accessToken]);

  useEffect(() => {
    if (!mode || mode === 'notify') return;
    if (mode === 'create') {
      setSlotDate(new Date().toISOString().slice(0, 10));
      setStartHm('10:00');
      setEndHm('11:00');
      setMaxPart('8');
      setServiceId('');
      setError(null);
      setIsPersonal(initialPersonal ?? false);
      setSelectedClient(null);
      setClientSearch('');
      setClientResults([]);
      setPersonalComment('');
      setSelectedBranchId(branchId);
      return;
    }
    if (slot) {
      setSlotDate(slot.slot_date);
      setStartHm(toHm(slot.start_time));
      setEndHm(toHm(slot.end_time));
      setMaxPart(String(Math.max(1, slot.max_participants)));
      setServiceId(String(slot.service_id));
    }
    setError(null);
  }, [mode, slot, organizationId, branchId, accessToken, initialPersonal]);

  useEffect(() => {
    if (mode === 'notify') {
      setNotifyEvent('slot_reminder');
      setNotifyMessage('');
      setError(null);
    }
  }, [mode, slot?.id]);

  useEffect(() => {
    if (!isPersonal || !clientSearch.trim() || clientSearch.trim().length < 2) {
      setClientResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setClientSearching(true);
      try {
        const res = await listAdminClients(accessToken, { q: clientSearch.trim(), limit: 10 });
        setClientResults(res.items || []);
      } catch {
        setClientResults([]);
      } finally {
        setClientSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isPersonal, clientSearch, accessToken]);

  if (!mode) return null;

  const title =
    mode === 'create'
      ? 'Новый слот'
      : mode === 'edit'
        ? 'Изменить слот'
        : mode === 'move'
          ? 'Перенос слота'
          : 'Уведомление по слоту';

  async function onCreate() {
    const sid = Number.parseInt(serviceId, 10);
    if (!Number.isFinite(sid) || sid < 1) {
      setError('Выберите услугу.');
      return;
    }
    if (isPersonal) {
      if (!selectedClient) {
        setError('Выберите клиента.');
        return;
      }
    }
    const max = isPersonal ? 1 : Number.parseInt(maxPart, 10);
    if (!Number.isFinite(max) || max < 1) {
      setError('Укажите вместимость (число ≥ 1).');
      return;
    }
    const body = {
      ServiceID: sid,
      BranchID: selectedBranchId,
      SlotDate: slotDate,
      StartTime: startHm.length === 5 ? `${startHm}:00` : startHm,
      EndTime: endHm.length === 5 ? `${endHm}:00` : endHm,
      MaxParticipants: max,
      IsAvailable: true,
      ...(trainerUserId != null && trainerUserId > 0 ? { TrainerUserID: trainerUserId } : {})
    };
    setBusy(true);
    setError(null);
    try {
      const slot = await createScheduleSlot(accessToken, body, organizationId);
      if (isPersonal && selectedClient) {
        try {
          await createBookingForClient(accessToken, {
            slot_id: slot.id,
            client_id: selectedClient.id,
            comment: personalComment.trim() || null
          });
        } catch (bookErr) {
          setError(
            `Слот создан (ID ${slot.id}), но запись клиента не удалась: ${
              bookErr instanceof ApiError ? bookErr.detail : bookErr instanceof Error ? bookErr.message : 'Ошибка'
            }`
          );
          setBusy(false);
          return;
        }
      }
      onCommitted();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось создать слот');
    } finally {
      setBusy(false);
    }
  }

  async function onPatch() {
    if (!slot) return;
    const max = Number.parseInt(maxPart, 10);
    const patch: Record<string, unknown> = {
      SlotDate: slotDate,
      StartTime: startHm.length === 5 ? `${startHm}:00` : startHm,
      EndTime: endHm.length === 5 ? `${endHm}:00` : endHm
    };
    if (Number.isFinite(max) && max >= 1) {
      patch.MaxParticipants = max;
    }
    setBusy(true);
    setError(null);
    try {
      await patchScheduleSlot(accessToken, slot.id, patch, { organizationId });
      onCommitted();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  async function onValidateMove() {
    if (!slot) return;
    const body = {
      slot_date: slotDate,
      start_time: startHm.length === 5 ? `${startHm}:00` : startHm,
      end_time: endHm.length === 5 ? `${endHm}:00` : endHm
    };
    setBusy(true);
    setError(null);
    try {
      await validateScheduleSlotMove(accessToken, slot.id, body, organizationId);
      setError(null);
      window.alert('Проверка пройдена: перенос допустим. Нажмите «Применить перенос».');
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Перенос невозможен');
    } finally {
      setBusy(false);
    }
  }

  async function onApplyMove() {
    if (!slot) return;
    const body = {
      slot_date: slotDate,
      start_time: startHm.length === 5 ? `${startHm}:00` : startHm,
      end_time: endHm.length === 5 ? `${endHm}:00` : endHm
    };
    setBusy(true);
    setError(null);
    try {
      await moveScheduleSlot(accessToken, slot.id, body, organizationId);
      onCommitted();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось перенести');
    } finally {
      setBusy(false);
    }
  }

  async function onSendNotify() {
    if (!slot) return;
    setBusy(true);
    setError(null);
    try {
      await notifyScheduleSlot(accessToken, slot.id, {
        event_type: notifyEvent,
        message: notifyMessage.trim() || undefined,
        target_roles: ['clients']
      });
      onCommitted();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-[var(--text)]">{title}</h2>
          <button
            type="button"
            className="rounded-full px-2 py-1 text-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)]"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {error ? (
          <EmbedMessageBlock className="mt-3" variant="error" size="field" role="alert">
            {error}
          </EmbedMessageBlock>
        ) : null}

        {mode === 'create' ? (
          <div className="mt-4 space-y-3 text-sm">
            {/* Персональная тренировка toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[var(--text-muted)]">Персональная тренировка</span>
              <button
                type="button"
                role="switch"
                aria-checked={isPersonal}
                onClick={() => setIsPersonal(!isPersonal)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                  isPersonal ? 'bg-[var(--accent)]' : 'bg-[var(--bg-soft)] border border-[var(--border)]'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                    isPersonal ? 'translate-x-[18px]' : 'translate-x-[3px]'
                  }`}
                />
              </button>
            </label>
            {isPersonal && me?.trainer_placements && me.trainer_placements.length > 1 && (
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Филиал</span>
                <select
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 text-[var(--text)]"
                  value={String(selectedBranchId)}
                  onChange={(ev) => {
                    const bid = Number(ev.target.value);
                    setSelectedBranchId(bid);
                    setServiceId('');
                  }}
                >
                  {me.trainer_placements.map((p) => (
                    <option key={p.branch_id} value={String(p.branch_id)}>
                      {p.branch_name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              <span className="mb-1 block text-[var(--text-muted)]">Услуга</span>
              {servicesLoading ? (
                <div className="h-9 animate-pulse rounded bg-[var(--bg-soft)]" />
              ) : (
                <select
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 text-[var(--text)]"
                  value={serviceId}
                  onChange={(ev) => setServiceId(ev.target.value)}
                >
                  <option value="">— выберите —</option>
                  {services.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="block">
              <span className="mb-1 block text-[var(--text-muted)]">Дата</span>
              <input
                type="date"
                className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                value={slotDate}
                onChange={(ev) => setSlotDate(ev.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Начало</span>
                <input
                  type="time"
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={startHm}
                  onChange={(ev) => setStartHm(ev.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Конец</span>
                <input
                  type="time"
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={endHm}
                  onChange={(ev) => setEndHm(ev.target.value)}
                />
              </label>
            </div>
            {isPersonal ? (
              <>
                <div className="block">
                  <span className="mb-1 block text-[var(--text-muted)]">Клиент</span>
                  {selectedClient ? (
                    <div className="flex items-center gap-2 rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-1.5">
                      <span className="flex-1 text-[var(--text)]">
                        {selectedClient.full_name || selectedClient.email}
                      </span>
                      <button
                        type="button"
                        className="text-[var(--text-muted)] hover:text-[var(--text)]"
                        onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Поиск по имени, телефону, email…"
                        className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 text-[var(--text)]"
                        value={clientSearch}
                        onChange={(ev) => setClientSearch(ev.target.value)}
                      />
                      {clientSearching && (
                        <div className="absolute right-2 top-2 text-xs text-[var(--text-muted)]">…</div>
                      )}
                      {clientResults && clientResults.length > 0 && (
                        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-40 overflow-y-auto rounded border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg">
                          {clientResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--bg-soft)] text-[var(--text)]"
                              onClick={() => {
                                setSelectedClient(c);
                                setClientSearch('');
                                setClientResults([]);
                              }}
                            >
                              <span className="font-medium">{c.full_name || c.email}</span>
                              {c.phone ? <span className="ml-2 text-[var(--text-muted)]">{c.phone}</span> : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <label className="block">
                  <span className="mb-1 block text-[var(--text-muted)]">Комментарий</span>
                  <textarea
                    className="min-h-16 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-2 text-[var(--text)]"
                    value={personalComment}
                    onChange={(ev) => setPersonalComment(ev.target.value)}
                    placeholder="Пожелания к тренировке…"
                  />
                </label>
              </>
            ) : (
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Макс. участников</span>
                <input
                  type="number"
                  min={1}
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={maxPart}
                  onChange={(ev) => setMaxPart(ev.target.value)}
                />
              </label>
            )}
          </div>
        ) : null}

        {mode === 'edit' || mode === 'move' ? (
          <div className="mt-4 space-y-3 text-sm">
            {slot ? (
              <p className="text-[var(--text-muted)]">
                {slot.service_name} · сейчас {formatSlotDayShort(slot.slot_date)} {toHm(slot.start_time)}—{toHm(slot.end_time)}
              </p>
            ) : null}
            <label className="block">
              <span className="mb-1 block text-[var(--text-muted)]">Дата</span>
              <input
                type="date"
                className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                value={slotDate}
                onChange={(ev) => setSlotDate(ev.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Начало</span>
                <input
                  type="time"
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={startHm}
                  onChange={(ev) => setStartHm(ev.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Конец</span>
                <input
                  type="time"
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={endHm}
                  onChange={(ev) => setEndHm(ev.target.value)}
                />
              </label>
            </div>
            {mode === 'edit' ? (
              <label className="block">
                <span className="mb-1 block text-[var(--text-muted)]">Макс. участников</span>
                <input
                  type="number"
                  min={1}
                  className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                  value={maxPart}
                  onChange={(ev) => setMaxPart(ev.target.value)}
                />
              </label>
            ) : null}
          </div>
        ) : null}

        {mode === 'notify' && slot ? (
          <div className="mt-4 space-y-3 text-sm">
            <p className="text-[var(--text-muted)]">
              {slot.service_name} · {formatSlotDayShort(slot.slot_date)} {toHm(slot.start_time)}
            </p>
            <label className="block">
              <span className="mb-1 block text-[var(--text-muted)]">Тип</span>
              <select
                className="h-9 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2"
                value={notifyEvent}
                onChange={(ev) => setNotifyEvent(ev.target.value)}
              >
                <option value="slot_reminder">Напоминание</option>
                <option value="slot_custom">Своё сообщение</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[var(--text-muted)]">Текст (необязательно)</span>
              <textarea
                className="min-h-24 w-full rounded border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-2 text-[var(--text)]"
                value={notifyMessage}
                onChange={(ev) => setNotifyMessage(ev.target.value)}
              />
            </label>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {mode === 'create' ? (
            <button
              type="button"
              disabled={busy}
              className="rounded-xl bg-[var(--accent-bg)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg)] disabled:opacity-50"
              onClick={() => void onCreate()}
            >
              {busy ? 'Создаём…' : isPersonal ? 'Создать и записать' : 'Создать'}
            </button>
          ) : null}
          {mode === 'edit' ? (
            <button
              type="button"
              disabled={busy}
              className="rounded-xl bg-[var(--accent-bg)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg)] disabled:opacity-50"
              onClick={() => void onPatch()}
            >
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </button>
          ) : null}
          {mode === 'move' ? (
            <>
              <button
                type="button"
                disabled={busy}
                className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold text-[var(--text)]"
                onClick={() => void onValidateMove()}
              >
                Проверить
              </button>
              <button
                type="button"
                disabled={busy}
                className="rounded-xl bg-[var(--accent-bg)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg)] disabled:opacity-50"
                onClick={() => void onApplyMove()}
              >
                {busy ? 'Переносим…' : 'Применить перенос'}
              </button>
            </>
          ) : null}
          {mode === 'notify' ? (
            <button
              type="button"
              disabled={busy}
              className="rounded-xl bg-[var(--accent-bg)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg)] disabled:opacity-50"
              onClick={() => void onSendNotify()}
            >
              {busy ? 'Отправка…' : 'Отправить'}
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-xl px-4 py-2 text-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)]"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
